// ── Shared project-status registry (C1182) ──
//
// Every page used to hardcode STATUSES = ['pending','in_progress','on_fire','completed',
// 'canceled'] plus its own STATUS_LABELS/STATUS_CLASSES map. C1180/C1181 made statuses
// per-project (project_task_statuses table, GET/POST/PATCH/DELETE + PUT reorder at
// /api/projects/:id/statuses — see tt-api-tasks.md), so a fixed literal list can no
// longer describe what a project's board actually shows. This module is the single
// place every page loads a project's statuses from, replacing those per-file constants.
//
// Cached per project id (`_cache`) since most pages call loadStatuses() once per render
// and several call it more than once (filters + card + detail panel, say) — invalidated
// by the Workflow tab (settings.js) after every add/rename/recolor/delete/reorder so a
// stale list never lingers past the mutation that changed it.

import { api } from '../api.js';

const _cache = new Map();       // projectId -> Promise<StatusRow[]>
let _workspaceCache = null;     // Promise<{name,color}[]> — cross-project union

// ── Status label locale (C1190) ──
//
// This SPA has NO general i18n layer (unlike the Task App's src/client/i18n.js — see
// tt-i18n.md) — every other string here is a hardcoded English literal. This is a
// *minimal* label table for the 5 legacy status names only, values copied verbatim from
// the Task App's i18n.js LOCALES.en/.uk 'status.*' keys, so chip text can follow a
// project's `language` field (en/uk — set in settings.js's General tab) instead of always
// showing the raw slug. Custom per-project statuses (anything not in this table) still
// fall through to the humanizer below — never a general translation system.
const STATUS_LABELS = {
  en: {
    pending: 'Pending',
    in_progress: 'In Progress',
    on_fire: 'On Fire',
    completed: 'Completed',
    canceled: 'Canceled',
  },
  uk: {
    pending: 'Очікує',
    in_progress: 'В роботі',
    on_fire: 'Термінове',
    completed: 'Виконано',
    canceled: 'Скасовано',
  },
};

let _statusLocale = 'en';

// Call after loading a project (and again on live language change) so statusLabel()
// reflects that project's `language` field. Unknown/missing lang falls back to 'en'.
export function setStatusLocale(lang) {
  _statusLocale = STATUS_LABELS[lang] ? lang : 'en';
}

export function getStatusLocale() {
  return _statusLocale;
}

// Legacy 5, used only as a fallback when a fetch fails outright (network hiccup, an
// older/misconfigured deployment) — never surfaced as an editable list, just enough so a
// page doesn't render blank.
const FALLBACK_STATUSES = [
  { id: -1, name: 'pending', color: 'overlay1', display_order: 0, is_workflow_start: true, is_in_progress: false, is_workflow_complete: false, is_workflow_canceled: false, task_count: 0 },
  { id: -2, name: 'in_progress', color: 'blue', display_order: 1, is_workflow_start: false, is_in_progress: true, is_workflow_complete: false, is_workflow_canceled: false, task_count: 0 },
  { id: -3, name: 'on_fire', color: 'red', display_order: 2, is_workflow_start: false, is_in_progress: false, is_workflow_complete: false, is_workflow_canceled: false, task_count: 0 },
  { id: -4, name: 'completed', color: 'green', display_order: 3, is_workflow_start: false, is_in_progress: false, is_workflow_complete: true, is_workflow_canceled: false, task_count: 0 },
  { id: -5, name: 'canceled', color: 'overlay0', display_order: 4, is_workflow_start: false, is_in_progress: false, is_workflow_complete: false, is_workflow_canceled: true, task_count: 0 },
];

// This project's status registry, `display_order` ASC. Memoised per project id.
export async function loadStatuses(projectId) {
  if (!_cache.has(projectId)) {
    _cache.set(projectId, api.get(`/projects/${projectId}/statuses`)
      .then(res => (res.statuses || []).slice().sort((a, b) => a.display_order - b.display_order))
      .catch(() => FALLBACK_STATUSES));
  }
  return _cache.get(projectId);
}

// Drop this project's cached list — call after any Workflow-tab mutation (add, rename,
// recolor, delete, reorder, role move) so the next loadStatuses() call re-fetches.
export function invalidateStatuses(projectId) {
  _cache.delete(projectId);
}

// Cross-project union ({name, color} only, no role flags/task_count — those are
// per-project) for views spanning many projects: dashboard, workspace-tasks.
export async function loadWorkspaceStatuses() {
  if (!_workspaceCache) {
    _workspaceCache = api.get('/workspace/statuses')
      .then(res => res.statuses || [])
      .catch(() => FALLBACK_STATUSES.map(s => ({
        name: s.name, color: s.color,
        is_workflow_complete: s.is_workflow_complete, is_workflow_canceled: s.is_workflow_canceled,
      })));
  }
  return _workspaceCache;
}

export function invalidateWorkspaceStatuses() {
  _workspaceCache = null;
}

export function statusNames(statuses) {
  return (statuses || []).map(s => s.name);
}

// Locale-aware for the legacy 5 (C1190): probe STATUS_LABELS.en FIRST (not the active
// locale table directly) — same guard as the Task App's status-registry.js statusLabel(),
// because a miss there would otherwise mean "not one of the 5 known names", not "no uk
// translation for this key". A custom per-project status (anything outside the legacy 5,
// e.g. "qa_review") always humanizes: "qa_review" -> "Qa Review" — no label table entry
// exists or is needed for a name a project admin can type freely.
export function statusLabel(name) {
  const key = String(name || '');
  if (STATUS_LABELS.en[key] !== undefined) {
    return (STATUS_LABELS[_statusLocale] && STATUS_LABELS[_statusLocale][key]) || STATUS_LABELS.en[key];
  }
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// The status object holding a given workflow role, or null (e.g. a project mid-edit with
// no in-progress status momentarily assigned — the API only lets a role move, never
// clear outright, but a freshly-seeded/corrupted list is defensive-coded here too).
export function roleStatus(statuses, role) {
  const col = role === 'start' ? 'is_workflow_start'
    : role === 'in_progress' ? 'is_in_progress'
    : role === 'complete' ? 'is_workflow_complete'
    : role === 'canceled' ? 'is_workflow_canceled'
    : null;
  if (!col) return null;
  return (statuses || []).find(s => s[col]) || null;
}

// C1187 — "is this status closed" resolved via the complete OR canceled role, replacing
// every ad-hoc `status !== 'completed' && status !== 'canceled'` check in this app.
export function isClosedStatus(statuses, name) {
  if (!name) return false;
  const complete = roleStatus(statuses, 'complete');
  const canceled = roleStatus(statuses, 'canceled');
  return name === (complete && complete.name) || name === (canceled && canceled.name);
}

// Every status name in `statuses` that holds the complete or canceled role — for call
// sites that filter a name list (e.g. workspace/cross-project views) rather than test one
// name at a time.
export function closedNames(statuses) {
  return (statuses || []).filter(s => s.is_workflow_complete || s.is_workflow_canceled).map(s => s.name);
}

// `class`/`style` pair for a status badge — `--status-color` drives `.badge-status` in
// styles.css (`color-mix(in srgb, var(--status-color) ...)`), so any color token (or a
// plain CSS color string) works without a per-name CSS rule. Returns only the
// color-modifier class, matching the old `STATUS_CLASSES[s]` convention (e.g.
// `badge-pending`, no base `.badge`/`.sb-status-pill`/etc.) — combine with whichever
// shape class the call site already uses (`badge badge-status`, `sb-status-pill
// badge-status`, ...), same as every call site combined `.badge` + `STATUS_CLASSES[s]`
// itself before.
export function statusBadgeAttrs(status) {
  const color = (status && status.color) ? `var(--c-${status.color})` : 'var(--c-overlay1)';
  return { className: 'badge-status', style: `--status-color:${color}` };
}

// ── transliterateToSlug (C1190) ──
//
// Status *names* stay english snake_case slugs — they're the literal value written into
// `tasks.status`, matched by agents, and used as a workflow-role key across the API, Task
// App, and MCP server (see tt-task-statuses.md). The Workflow tab's add-status *title* is
// free text an admin can type in either project locale (en/uk, tt-i18n); this converts
// that title into a slug before it's ever sent to the API. Non-positional letters map 1:1;
// є/ї/й/ю/я/ё use their word-initial form (ye/yi/y/yu/ya/yo) vs. mid-word form
// (ie/i/i/iu/ia/io) per the KMU 55:2010 Ukrainian romanization table; "зг" is special-cased
// to "zgh" so it doesn't collide with ж ("zh"); ь/ъ/apostrophes are dropped, not converted
// to '_'. Nothing reusable existed anywhere in the repo — the only other slugify
// (api/src/routes/projects.js, module-private) is ASCII-only and strips Cyrillic entirely.
const CYRILLIC_MAP = {
  а: 'a', б: 'b', в: 'v', г: 'h', ґ: 'g', д: 'd', е: 'e', ж: 'zh', з: 'z',
  и: 'y', і: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ь: '', ъ: '', ы: 'y', э: 'e', // ru-only extras — admins type these too
};
// Word-initial vs. mid-word forms — checked against the still-untransliterated source char.
const POSITIONAL = {
  є: ['ye', 'ie'], ї: ['yi', 'i'], й: ['y', 'i'], ю: ['yu', 'iu'], я: ['ya', 'ia'], ё: ['yo', 'io'],
};
const WORD_CHAR_RE = /[a-zа-яіїєґ0-9]/;

export function transliterateToSlug(title) {
  // Apostrophes (incl. the Ukrainian soft-apostrophe и'я-style spellings) are dropped
  // outright and must NOT act as a word boundary — strip before the position-aware loop
  // so the vowel after one still reads as mid-word ("iu"/"ia"/…), not word-initial.
  const s = String(title || '').normalize('NFC').toLowerCase().replace(/['’ʼ`]/g, '');

  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === 'з' && s[i + 1] === 'г') { out += 'zgh'; i++; continue; } // digraph exception
    if (POSITIONAL[ch]) {
      const wordInitial = !WORD_CHAR_RE.test(s[i - 1] || '');
      out += POSITIONAL[ch][wordInitial ? 0 : 1];
      continue;
    }
    out += Object.prototype.hasOwnProperty.call(CYRILLIC_MAP, ch) ? CYRILLIC_MAP[ch] : ch;
  }

  // Fold Latin diacritics (café -> cafe) — must run AFTER the Cyrillic mapping above, never
  // before: NFD decomposes й -> и + combining breve and ї -> і + diaeresis, so stripping
  // combining marks first would silently turn "Йосип Їжак" into "Иосип Іжак" before this
  // function ever sees the real і/й — losing the word-initial forms above entirely.
  out = out.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  out = out.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (out.length > 64) out = out.slice(0, 64).replace(/_+$/g, '');
  return out;
}
